import React, { useState, useMemo } from "react";
import "./css/Contact.css";

const INITIAL = {
  firstName: "",
  lastName: "",
  email: "",
  phone: "",
  subject: "",   // back as free-text
  message: "",
  company: "" // honeypot (leave empty)
};

const validate = (values) => {
  const errors = {};
  if (!values.firstName.trim()) errors.firstName = "Please enter your first name.";
  if (!values.lastName.trim()) errors.lastName = "Please enter your last name.";

  if (!values.email.trim()) {
    errors.email = "Please enter your email.";
  } else {
    const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(values.email.trim());
    if (!emailOk) errors.email = "Please enter a valid email.";
  }

  if (values.phone.trim()) {
    const digits = values.phone.replace(/[^\d]/g, "");
    if (digits.length < 7) errors.phone = "Please enter a valid phone number.";
  }

  if (!values.subject.trim()) errors.subject = "Please enter a subject.";
  if (!values.message.trim()) errors.message = "Please write a short message.";
  if (values.message.length > 2000) errors.message = "Message is too long (max 2000 characters).";

  if (values.company) errors.company = "Spam detected.";
  return errors;
};

const Contact = () => {
  const [formData, setFormData] = useState(INITIAL);
  const [touched, setTouched] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const errors = useMemo(() => validate(formData), [formData]);
  const isValid = useMemo(() => Object.keys(errors).length === 0, [errors]);
  const messageCount = `${formData.message.length}/2000`;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((s) => ({ ...s, [name]: value }));
  };

  const handleBlur = (e) => {
    const { name } = e.target;
    setTouched((t) => ({ ...t, [name]: true }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setTouched({
      firstName: true,
      lastName: true,
      email: true,
      phone: true,
      subject: true,
      message: true
    });
    if (!isValid) return;

    try {
      setSubmitting(true);
      await new Promise((r) => setTimeout(r, 900));
      setSubmitted(true);
      setFormData(INITIAL);
      setTouched({});
    } finally {
      setSubmitting(false);
    }
  };

  const faqs = [
    { question: "How do I reset my password?", answer: "Click ‘Forgot Password’ on the login page and follow the instructions. You’ll receive an email with a reset link within minutes." },
    { question: "Are practice quizzes really free?", answer: "Yes. All practice quizzes are free with no hidden charges. You can access unlimited sessions anytime." },
    { question: "How are prizes distributed?", answer: "Prizes are distributed within 7 working days via bank transfer or digital wallet. Winners are notified via email and SMS." },
    { question: "Can schools register multiple students?", answer: "Yes, schools can use our bulk registration feature to add multiple students. Contact support if you need assistance." }
  ];

  const [openIdx, setOpenIdx] = useState(null);
  const toggleIdx = (idx) => setOpenIdx((v) => (v === idx ? null : idx));

  return (
    <div className="contact-page">
      <main className="contact-main">
        <div className="contact-container">
          <section className="contact-hero" aria-labelledby="contactHeading">
            <h1 id="contactHeading">Contact Us</h1>
            <p className="hero-sub">
              Have questions? We’re here to help. Reach out to our support team for any assistance.
            </p>
          </section>

          {submitted && (
            <div className="success-banner" role="status" aria-live="polite">
              <div className="success-icon" aria-hidden>✅</div>
              <div className="success-text">
                <h3>Thanks! Your message has been sent.</h3>
                <p>We usually reply within 24 hours. Meanwhile, check our FAQs below for quick answers.</p>
                <button type="button" className="btn-outline-secondary" onClick={() => setSubmitted(false)}>
                  Send another message
                </button>
              </div>
            </div>
          )}

          <section className="contact-grid" aria-label="Contact and Support">
            {/* Form Card */}
            <div className="card form-card form-card--wider">
              <h3 className="card-title">Send us a message</h3>
              <p className="card-sub">Fill out the form below and we’ll get back to you shortly.</p>

              <form className="contact-form" onSubmit={handleSubmit} noValidate>
                {/* Honeypot */}
                <input
                  type="text"
                  name="company"
                  value={formData.company}
                  onChange={handleChange}
                  tabIndex={-1}
                  autoComplete="off"
                  className="hp-field"
                  aria-hidden="true"
                />

                <div className="row two-cols">
                  <div className={`field ${touched.firstName && errors.firstName ? "has-error" : ""}`}>
                    <label htmlFor="firstName"><span className="label-text">First Name *</span></label>
                    <input
                      id="firstName"
                      type="text"
                      name="firstName"
                      placeholder="John"
                      value={formData.firstName}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      aria-invalid={touched.firstName && !!errors.firstName}
                      aria-describedby="firstNameHelp firstNameErr"
                      autoComplete="given-name"
                    />
                    <div id="firstNameHelp" className="hint">What should we call you?</div>
                    {touched.firstName && errors.firstName && <div id="firstNameErr" className="error-text">{errors.firstName}</div>}
                  </div>

                  <div className={`field ${touched.lastName && errors.lastName ? "has-error" : ""}`}>
                    <label htmlFor="lastName"><span className="label-text">Last Name *</span></label>
                    <input
                      id="lastName"
                      type="text"
                      name="lastName"
                      placeholder="Doe"
                      value={formData.lastName}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      aria-invalid={touched.lastName && !!errors.lastName}
                      aria-describedby="lastNameErr"
                      autoComplete="family-name"
                    />
                    {touched.lastName && errors.lastName && <div id="lastNameErr" className="error-text">{errors.lastName}</div>}
                  </div>
                </div>

                <div className={`field ${touched.email && errors.email ? "has-error" : ""}`}>
                  <label htmlFor="email"><span className="label-text">Email Address *</span></label>
                  <input
                    id="email"
                    type="email"
                    name="email"
                    placeholder="john@example.com"
                    value={formData.email}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    aria-invalid={touched.email && !!errors.email}
                    aria-describedby="emailHelp emailErr"
                    autoComplete="email"
                    inputMode="email"
                  />
                  <div id="emailHelp" className="hint">We’ll only use this to reply to you.</div>
                  {touched.email && errors.email && <div id="emailErr" className="error-text">{errors.email}</div>}
                </div>

                <div className={`field ${touched.phone && errors.phone ? "has-error" : ""}`}>
                  <label htmlFor="phone"><span className="label-text">Phone Number (optional)</span></label>
                  <input
                    id="phone"
                    type="tel"
                    name="phone"
                    placeholder="+91 98765 43210"
                    value={formData.phone}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    aria-invalid={touched.phone && !!errors.phone}
                    aria-describedby="phoneHelp phoneErr"
                    autoComplete="tel"
                    inputMode="tel"
                  />
                  <div id="phoneHelp" className="hint">Include country/area code if outside India.</div>
                  {touched.phone && errors.phone && <div id="phoneErr" className="error-text">{errors.phone}</div>}
                </div>

                {/* Subject as a free-text input */}
                <div className={`field ${touched.subject && errors.subject ? "has-error" : ""}`}>
                  <label htmlFor="subject"><span className="label-text">Subject *</span></label>
                  <input
                    id="subject"
                    type="text"
                    name="subject"
                    placeholder="How can we help you?"
                    value={formData.subject}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    aria-invalid={touched.subject && !!errors.subject}
                    aria-describedby="subjectErr"
                  />
                  {touched.subject && errors.subject && <div id="subjectErr" className="error-text">{errors.subject}</div>}
                </div>

                {/* Wider message field */}
                <div className={`field message-field ${touched.message && errors.message ? "has-error" : ""}`}>
                  <label htmlFor="message"><span className="label-text">Message *</span></label>
                  <textarea
                    id="message"
                    className="wide-message"
                    name="message"
                    placeholder="Please describe your question or concern in detail..."
                    rows={10}
                    maxLength={2000}
                    value={formData.message}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    aria-invalid={touched.message && !!errors.message}
                    aria-describedby="messageCount messageErr"
                  />
                  <div className="field-meta">
                    <span id="messageCount" className="char-counter">{messageCount}</span>
                  </div>
                  {touched.message && errors.message && <div id="messageErr" className="error-text">{errors.message}</div>}
                </div>

                <button type="submit" className="btn-submit" disabled={submitting || !isValid} aria-busy={submitting ? "true" : "false"}>
                  {submitting ? "Sending…" : "Send Message"}
                </button>

                {!isValid && (
                  <p className="form-footnote" role="alert">
                    Please fix the highlighted fields and try again.
                  </p>
                )}
              </form>
            </div>

            {/* Right Column */}
            <div className="right-column">
              <div className="card info-card">
                <h4 className="card-title small">Get in Touch</h4>
                <p className="card-sub small">Multiple ways to reach our support team</p>

                <ul className="info-list">
                  <li>
                    <div className="info-icon email" aria-hidden>✉️</div>
                    <div className="info-text">
                      <div className="info-title">Email Support</div>
                      <div className="info-meta">info@mydocmaster.com <span className="muted">Response within 24 hours</span></div>
                    </div>
                  </li>
                  <li>
                    <div className="info-icon phone" aria-hidden>📞</div>
                    <div className="info-text">
                      <div className="info-title">Phone Support</div>
                      <div className="info-meta">+91 92894 40049 <span className="muted">Mon–Sat, 9 AM – 6 PM IST</span></div>
                    </div>
                  </li>
                  <li>
                    <div className="info-icon place" aria-hidden>📍</div>
                    <div className="info-text">
                      <div className="info-title">Office Address</div>
                      <div className="info-meta">
                        DocMaster<br />A 75 Sector 63 Noida - 201301<br />Uttar Pradesh, India
                      </div>
                    </div>
                  </li>
                  <li>
                    <div className="info-icon clock" aria-hidden>⏰</div>
                    <div className="info-text">
                      <div className="info-title">Business Hours</div>
                      <div className="info-meta">
                        Monday – Saturday: 9:00 AM – 6:00 PM<br />
                        Saturday: 10:00 AM – 4:00 PM<br />
                        Sunday: Closed
                      </div>
                    </div>
                  </li>
                </ul>
              </div>

              <div className="card faq-card">
                <h4 className="card-title small">Frequently Asked Questions</h4>
                <p className="card-sub small">Quick answers to common questions</p>

                <div className="faq-list" role="list">
                  {faqs.map((faq, i) => {
                    const open = openIdx === i;
                    const qId = `faq-q-${i}`;
                    const aId = `faq-a-${i}`;
                    return (
                      <div key={i} className={`faq-item ${open ? "active" : ""}`} role="listitem">
                        <button
                          className="faq-question"
                          aria-expanded={open}
                          aria-controls={aId}
                          id={qId}
                          onClick={() => toggleIdx(i)}
                          type="button"
                        >
                          <span className="faq-q">{faq.question}</span>
                          <span className="faq-toggle" aria-hidden>{open ? "−" : "+"}</span>
                        </button>
                        <div id={aId} role="region" aria-labelledby={qId} className={`faq-answer ${open ? "show" : ""}`}>
                          <div className="faq-a">{faq.answer}</div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </section>
        </div>
      </main>
    </div>
  );
};

export default Contact;
